// Cart-page ceiling for studio-designed lines.
//
// WHY: the studio caps the WHOLE CART at `bulk.min_qty - 1` studio pieces (it
// counts every studio line already in the cart before it adds one). That cap
// lives in the studio. Shopify's cart page has its own quantity box, and typing
// 50 into it walks straight past the cap: into the range where the weight-band
// shipping rates stop being exact, which is the whole reason the bulk path
// exists.
//
// WHAT THIS IS, HONESTLY: a guard, not enforcement. It watches the cart's own
// quantity controls and pulls the studio pieces back to the ceiling via
// /cart/change.js. Someone who disables JavaScript, or posts to /cart/change.js
// directly, still gets past it: real enforcement needs a Shopify Function
// (cart transform / checkout validation), which is an app surface this repo
// deliberately does not have. See TODO.md.
//
// It only touches lines the studio made: a line carrying `_pf_placements`.
// Blanks bought off a normal product page are none of its business, and the
// surcharge lines (no `_pf_placements`) are not counted: assets/ds-cart-sync.js
// pins them to the garment quantities after any change, so they follow on
// their own.
//
// THE SAME RULE AS THE STUDIO (polish CRT-8). This used to check each line on
// its own (9 per line), so a cart of 5 + 5 of two designs passed here while the
// studio refused it. It now counts the studio pieces in the whole cart, takes the
// excess off the line that was just changed, then (for a cart that arrived over)
// off the others from the last to the first, never below 1.
//
// THE MESSAGE STAYS ON SCREEN. The old message was written into the first
// [role=status] on the page (or an alert) and the page reloaded straight after,
// so nobody ever read it. Now it is kept in sessionStorage across the reload and
// shown once above the line list, with a link back to the design.

(function () {
  'use strict';

  // The studio caches /config in localStorage (6h TTL, see
  // sections/design-studio.liquid). Reading it means the ceiling comes from the
  // same source the studio uses, with no theme setting to drift out of sync
  // with the catalog.
  //
  // THE KEY IS VERSIONED AND THIS FILE CANNOT SEE THE BUMP. It was pinned to
  // `designStudio:config:v1` while the section had already moved to v3 (and is
  // now on v4), so this lookup had never once succeeded and the ceiling always
  // came from FALLBACK_MIN. It happened to be harmless: the fallback and the
  // committed min_qty are both 10: but it meant the cart ceiling would
  // silently ignore any change to min_qty, which is exactly what reading the
  // config was supposed to prevent.
  //
  // So: find whatever version is actually there, newest first, instead of
  // naming one. A bump in the section no longer needs a matching edit here.
  const CONFIG_KEY_PREFIX = 'designStudio:config:';
  const FALLBACK_MIN = 10;
  const STUDIO_PROP = '_pf_placements';
  const CART_EVENT = 'shopify:cart:lines-update';
  const SYNC_SOURCE = 'ds-cart-sync';
  const NOTE_KEY = 'tcz-cart-cap';
  const RELOADS_KEY = 'tcz-cart-cap-reloads';

  let ceiling = null;     // max studio pieces allowed in the whole cart
  let busy = false;
  let again;              // a pass asked for while one was running (undefined = none)
  let lastKey = null;     // the line the shopper touched last
  let syncWait = null;    // ds-cart-sync's write in flight, if any

  /** The most recently versioned cached config, or null. */
  function readCachedConfig() {
    let best = null, bestV = -1;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || k.indexOf(CONFIG_KEY_PREFIX) !== 0) continue;
      // `v12` must beat `v9`, so compare as a NUMBER, not as a string.
      const v = Number(k.slice(CONFIG_KEY_PREFIX.length).replace(/^v/, ''));
      if (!Number.isFinite(v) || v <= bestV) continue;
      bestV = v;
      best = k;
    }
    return best ? localStorage.getItem(best) : null;
  }

  /** The threshold, from the studio's own cached /config where available. */
  function resolveCeiling() {
    if (ceiling != null) return ceiling;
    let min = 0;
    try {
      const raw = readCachedConfig();
      if (raw) min = Number(JSON.parse(raw)?.cfg?.bulk?.min_qty) || 0;
    } catch (_) { /* private mode, cleared storage, changed shape */ }
    // A shopper who reached the cart without opening the studio has no cache.
    // The fallback matches the committed shipping_policy.bulk.min_qty; if that
    // moves, move this too.
    return (ceiling = (min > 1 ? min : FALLBACK_MIN) - 1);
  }

  const isStudioLine = (item) =>
    !!(item && item.properties && Object.prototype.hasOwnProperty.call(item.properties, STUDIO_PROP));

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const samePath = (href) => typeof href === 'string' && /^\/(?!\/)/.test(href);

  async function getCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.ok ? res.json() : null;
  }

  function message(max) {
    return `The cart holds up to ${max} pieces from the studio. ` +
      `For ${max + 1} or more, open your design, press Review, then Get a quote.`;
  }

  /** The link back to a line's design: its title link (the design, or the product page). */
  function linkFor(key) {
    let row = null;
    try { row = key ? document.querySelector(`[data-key="${CSS.escape(key)}"]`) : null; } catch (_) { /* old browser */ }
    const a = row && row.querySelector('a.cart-items__title');
    const href = a ? a.getAttribute('href') : null;
    return samePath(href) ? href : null;
  }

  /** A reload that did not settle the cart must not loop: at most 3 in 20 seconds. */
  function mayReload() {
    try {
      const now = Date.now();
      const recent = (JSON.parse(sessionStorage.getItem(RELOADS_KEY) || '[]') || []).filter((t) => now - t < 20000);
      if (recent.length >= 3) return false;
      recent.push(now);
      sessionStorage.setItem(RELOADS_KEY, JSON.stringify(recent));
    } catch (_) { /* no storage: the cart is fixed, one reload is still fine */ }
    return true;
  }

  const firstTable = () => document.querySelector('main .cart-items__table') || document.querySelector('.cart-items__table');

  /* The note shown on this page. The theme re-renders the cart by morphing the
   * section from fresh server HTML, which has no note in it: ds-cart-sync's fee
   * re-pin right after the reload does exactly that. So the note is put back
   * whenever a re-render drops it, until the shopper changes the cart themselves
   * (then it may no longer be true, and a new pass writes a new one). */
  let note = null;
  let noteWatch = null;
  function placeNote() {
    if (!note || note.isConnected) return;
    const table = firstTable();
    if (table && table.parentNode) table.parentNode.insertBefore(note, table);
  }
  function dropNote() {
    if (noteWatch) noteWatch.disconnect();
    noteWatch = null;
    note = null;
  }

  /** Show the kept message once, above the line list (the page's, else the drawer's). */
  function showKept() {
    let kept = null;
    try {
      kept = JSON.parse(sessionStorage.getItem(NOTE_KEY) || 'null');
      sessionStorage.removeItem(NOTE_KEY);
    } catch (_) { return; }
    if (!kept || typeof kept.text !== 'string' || document.querySelector('.sp-cart-cap')) return;
    const table = firstTable();
    if (!table || !table.parentNode) return;

    const p = document.createElement('p');
    p.className = 'sp-cart-cap';
    p.setAttribute('role', 'status');
    p.textContent = kept.text;
    if (samePath(kept.link)) {
      const a = document.createElement('a');
      a.href = kept.link;
      a.textContent = 'Open my design';
      p.append(' ', a);
    }
    note = p;
    placeNote();
    if ('MutationObserver' in window) {
      noteWatch = new MutationObserver(placeNote);
      noteWatch.observe(document.body, { childList: true, subtree: true });
    }

    // Changed in the drawer, away from the cart page: on a phone the drawer does
    // not reopen by itself after a reload, so open it where the shopper was.
    if (kept.where === 'drawer' && !table.closest('main')) {
      const drawer = table.closest('theme-drawer');
      if (drawer && !drawer.hasAttribute('open')) {
        customElements.whenDefined('theme-drawer').then(() => {
          try { drawer.open(); } catch (_) { /* the message is still in the drawer */ }
        });
      }
    }
  }

  /** Pull the studio pieces back to the ceiling, then reload the cart. */
  async function enforce(changedKey, where) {
    if (busy) { again = changedKey || null; return; }
    busy = true;
    try {
      const max = resolveCeiling();
      const cart = await getCart();
      if (!cart || !Array.isArray(cart.items)) return;

      const studio = cart.items.filter(isStudioLine);
      const total = studio.reduce((n, i) => n + (Number(i.quantity) || 0), 0);
      let excess = total - max;
      if (excess <= 0) return;

      // The line just changed first, then the others from the last to the first.
      const changed = changedKey ? studio.find((i) => i.key === changedKey) : null;
      const order = changed ? [changed] : [];
      for (let k = studio.length - 1; k >= 0; k--) if (studio[k] !== changed) order.push(studio[k]);

      let reduced = null;
      // One /cart/change.js at a time: Shopify's cart endpoints are not safe to
      // call in parallel, and a lost update here is a wrong cart.
      for (const item of order) {
        if (excess <= 0) break;
        const q = Number(item.quantity) || 0;
        const cut = Math.min(excess, q - 1);
        if (cut <= 0) continue;
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: q - cut }),
        });
        if (!res.ok) break;
        excess -= cut;
        if (!reduced) reduced = item;
      }
      if (!reduced) return;

      try {
        sessionStorage.setItem(NOTE_KEY, JSON.stringify({ text: message(max), link: linkFor(reduced.key), where: where || 'page' }));
      } catch (_) { /* the cart is still fixed; only the note is lost */ }

      // Let a fee re-pin already in flight land before the page goes away; the
      // reloaded page checks the fees again either way (ds-cart-sync runs on load).
      if (syncWait) await Promise.race([syncWait.catch(() => {}), sleep(3000)]);
      if (mayReload()) window.location.reload();
    } catch (_) {
      /* A cart we cannot read must not break the page. */
    } finally {
      busy = false;
      if (again !== undefined) {
        const key = again;
        again = undefined;
        enforce(key, where);
      }
    }
  }

  // The theme re-renders cart sections after every quantity change, so listen
  // on the document rather than binding to inputs that get replaced. Typing a
  // number fires `change`; the + and - buttons do not, but both end in the
  // theme's lines-update event, which names the line and carries a promise that
  // settles once the change has landed. Read the cart only after that: read too
  // early, /cart.js still holds the old quantity.
  const keyOf = (target) => {
    const row = target && target.closest ? target.closest('[data-key]') : null;
    return row ? row.dataset.key : null;
  };
  document.addEventListener('change', (e) => {
    if (e.target && e.target.matches && e.target.matches('input[type="number"], .quantity-selector input')) {
      lastKey = keyOf(e.target) || lastKey;
    }
  });
  document.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('.quantity-selector button') : null;
    if (btn) lastKey = keyOf(btn) || lastKey;
  }, true);

  function onCartEvent(event) {
    if (event && event.source === SYNC_SOURCE) {
      // Fee lines only, which are not counted. Remember the write so a reload waits for it.
      if (event.promise && typeof event.promise.then === 'function') syncWait = event.promise;
      return;
    }
    // The row the shopper touched wins over the event's own line id: the theme
    // names that line by its position among the ROWS (cartItemRows[line - 1]),
    // while `line` counts every cart line, the folded-away fee lines included,
    // so with a fee line ahead of it the event names the row below.
    dropNote();
    const line = event && Array.isArray(event.lines) ? event.lines[0] : null;
    const key = lastKey || (line && (line.id || line.key)) || null;
    lastKey = null;
    const target = event && event.target;
    const where = target && target.closest && target.closest('cart-drawer-component') ? 'drawer' : 'page';
    const run = () => enforce(key, where);
    const promise = event && event.promise;
    if (promise && typeof promise.then === 'function') promise.then(run, () => {});
    else run();
  }
  document.addEventListener(CART_EVENT, onCartEvent);
  document.addEventListener('cart:updated', onCartEvent);

  function start() {
    showKept();
    enforce(null, 'page');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
